import React, { useState, useEffect, useRef } from "react";
import { 
  Calendar, ArrowRight, CheckCircle2, PauseCircle, Search,
  X, RefreshCw, SkipForward, AlertTriangle
} from "lucide-react";
import { 
  useCancelSubscriptionMutation, 
  useChangeBlilingCycleMutation, 
  useLazyCustomerSubscriptionDashboardQuery, 
  useLazyGetSubscriptionByIdQuery, 
  useLazyMySubscriptionQuery, 
  usePauseSubscriptionMutation, 
  useResumeSubscriptionMutation, 
  useSkipNextDeliverySubscriptionMutation, 
  useUpdateSubscriptionQuantityMutation 
} from "../../../redux/actions/Customer/profileCustomer";
import { useAlert } from "../../../components/customer/AlertContext";

interface Props {}

const CustomerSubscriptions: React.FC<Props> = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  
  // Local states for updating fields
  const [editQty, setEditQty] = useState<number>(1);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [editCycle, setEditCycle] = useState<number>(1);

  const { showConfirm } = useAlert();

  // RTK Query Hooks
  const [getDashboard, { data: dashboard, isFetching: loadingDash }] = useLazyCustomerSubscriptionDashboardQuery();
  const [getList, { data: subList, isFetching: loadingList }] = useLazyMySubscriptionQuery();
  const [getDetails, { data: details, isFetching: loadingDetails }] = useLazyGetSubscriptionByIdQuery();

  // RTK Mutation Hooks
  const [pauseSub, { isLoading: isPausing }] = usePauseSubscriptionMutation();
  const [resumeSub, { isLoading: isResuming }] = useResumeSubscriptionMutation();
  const [cancelSub, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
  const [skipDelivery, { isLoading: isSkipping }] = useSkipNextDeliverySubscriptionMutation();
  const [updateQty, { isLoading: isUpdatingQty }] = useUpdateSubscriptionQuantityMutation();
  const [changeBilling, { isLoading: isChangingBilling }] = useChangeBlilingCycleMutation();

  const isMutating = isPausing || isResuming || isCancelling || isSkipping || isUpdatingQty || isChangingBilling;

  useEffect(() => {
    getDashboard();
    getList();
  }, [getDashboard, getList]);

  

  useEffect(() => {
    if (details) {
      setEditQty(details.quantity);
      setEditCycle(details.billingCycle);
    }
  }, [details]);

  const refreshAllData = () => {
    getDashboard();
    getList();
    if (selectedId) getDetails(selectedId);
  };

  const handleMutation = async (actionFn: () => Promise<any>, successMsg: string) => {
    try {
      await actionFn();
      refreshAllData();
    } catch {
      alert("Something went wrong. Please try again.");
    }
  };

  // Safe Confirmation Prompts
  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
    showConfirm({
      title,
      message,
      type: "warning",
      confirmText: "Yes, Proceed",
      cancelText: "No, Go Back",
      showCancel: true,
      onConfirm
    });
  };

  const getStatusBadge = (status: string) => {
    if (status === 'Active') return "bg-emerald-50 text-emerald-700 border-emerald-100";
    if (status === 'Paused') return "bg-amber-50 text-amber-700 border-amber-100";
    if (status === 'Cancelled') return "bg-rose-50 text-rose-700 border-rose-100";
    return "bg-gray-50 text-gray-600 border-gray-200";
  };

  const getCycleName = (cycle: number) => {
    if (cycle === 1) return "Weekly";
    if (cycle === 2) return "Monthly";
    return "Subscription";
  };

  const handleCancel = () => {
    showConfirm({ 
      title: "Cancel Subscription", 
      message: "Are you sure you want to cancel this subscription?", 
      type: "error", 
      confirmText: "Yes, Cancel Plan",
      cancelText: "No, Keep Plan",
      showCancel: true,
      onConfirm: () => {
        handleMutation(() => cancelSub(details!.id).unwrap(), "Subscription cancelled successfully.");
      }
    });
  };

  // Check if at least one order/renewal is completed
  const hasCompletedFirstOrder = Boolean(
    details?.renewals && details.renewals.length > 1 && details.renewals.some((r) => r.paymentSucceeded)
  );

  const filteredList = subList?.filter(item => {
    const matchesSearch = item.productName.toLowerCase().includes(search.toLowerCase()) || item.id.toString().includes(search);
    
    if (statusFilter === "All") return matchesSearch;
    if (statusFilter === "Active") return matchesSearch && item.status === 'Active';
    if (statusFilter === "Paused") return matchesSearch && item.status === 'Paused';
    if (statusFilter === "Cancelled") return matchesSearch && item.status === 'Cancelled';
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#FAFBF8] p-3 sm:p-6 md:p-8 space-y-6 text-gray-800 w-full max-w-7xl mx-auto">
      
      {/* Header Panel */}
      <div className="border-b pb-4">
        <h1 className="text-xl font-black text-gray-950 tracking-tight sm:text-2xl">My Subscriptions</h1>
        <p className="text-xs text-gray-500 mt-0.5">View and manage your recurring items, quantities, and delivery dates.</p>
      </div>

      {/* Dashboard Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-bold">
        <div className="bg-white border border-[#DDE7D8] p-3 rounded-xl">
          <span className="text-gray-400 text-[10px] uppercase block">Active</span>
          <span className="text-emerald-700 text-base sm:text-lg mt-0.5 block">{loadingDash ? "..." : dashboard?.activeSubscriptions ?? 0}</span>
        </div>
        <div className="bg-white border border-[#DDE7D8] p-3 rounded-xl">
          <span className="text-gray-400 text-[10px] uppercase block">Paused</span>
          <span className="text-amber-600 text-base sm:text-lg mt-0.5 block">{loadingDash ? "..." : dashboard?.pausedSubscriptions ?? 0}</span>
        </div>
        <div className="bg-white border border-[#DDE7D8] p-3 rounded-xl">
          <span className="text-gray-400 text-[10px] uppercase block">Next Delivery</span>
          <span className="text-gray-900 text-xs sm:text-sm truncate mt-1 block">
            {loadingDash ? "..." : dashboard?.nextDeliveryDate ? new Date(dashboard.nextDeliveryDate).toLocaleDateString() : "No active orders"}
          </span>
        </div>
        <div className="bg-white border border-[#DDE7D8] p-3 rounded-xl">
          <span className="text-gray-400 text-[10px] uppercase block">Upcoming Total</span>
          <span className="text-[#2F5E2E] text-base sm:text-lg mt-0.5 block">AED {loadingDash ? "..." : dashboard?.estimatedUpcomingAmount?.toFixed(2) ?? "0.00"}</span>
        </div>
      </div>

      {/* Interactive Tabs & Search Filtering */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between bg-white p-3 rounded-xl border border-[#DDE7D8]">
        <div>
          <label className="text-[9px] text-gray-400 font-bold uppercase block mb-1">Filter by Status</label>
          <div className="bg-gray-100 p-0.5 rounded-lg flex gap-0.5 text-xs font-bold">
            {["All", "Active", "Paused", "Cancelled"].map(tab => (
              <button 
                key={tab} 
                onClick={() => setStatusFilter(tab)} 
                className={`px-3 py-1 rounded-md transition-all ${statusFilter === tab ? "bg-[#2F5E2E] text-white shadow-xs" : "text-gray-500 hover:text-gray-900"}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="relative flex items-center w-full lg:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute left-3" />
          <input 
            type="text" 
            placeholder="Search items by product name or ID..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="w-full pl-9 pr-4 py-2 bg-[#FAFBF8] border border-[#DDE7D8] rounded-xl text-xs focus:ring-1 focus:ring-[#2F5E2E] outline-hidden" 
          />
        </div>
      </div>

      {/* Subscription List Section */}
      <div>
        {loadingList ? (
          <div className="bg-white border rounded-xl p-12 text-center text-xs text-gray-500"><RefreshCw className="w-4 h-4 animate-spin inline mr-2 text-[#2F5E2E]" /> Loading items...</div>
        ) : !filteredList || filteredList.length === 0 ? (
          <div className="bg-white border border-dashed rounded-xl p-12 text-center text-xs text-gray-400">No matching subscriptions found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredList.map(item => (
              <div key={item.id} className="bg-white border border-[#DDE7D8] rounded-xl overflow-hidden p-3.5 flex flex-col justify-between shadow-xs hover:shadow-md transition">
                <div className="flex gap-3.5 items-start">
                  <img src={item.imageUrl || "https://images.unsplash.com/photo-1589924691995-400dc9ebd1cc?w=150"} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-100 shrink-0 bg-gray-50" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex justify-between items-start gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${getStatusBadge(item.status)}`}>
                        {item.status === '"Active"' ? "Active" : item.status === 'Paused' ? "Paused" : item.status === 'Cancelled' ? "Cancelled" : "Inactive"}
                      </span>
                      <span className="text-[10px] text-gray-400">ID: #{item.id}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-xs sm:text-sm truncate pt-0.5">{item.productName}</h3>
                    <p className="text-gray-400 text-[10px] sm:text-[11px]">{getCycleName(item.billingCycle)} | Qty: {item.quantity}</p>
                  </div>
                </div>

                <div className="border-t border-gray-100 mt-4 pt-3 flex items-center justify-between text-[11px] sm:text-xs">
                  <div className="flex items-center gap-1 text-gray-500">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span>Next Date: {new Date(item.nextBillingDate).toLocaleDateString()}</span>
                  </div>
                  <button onClick={() => { setSelectedId(item.id); getDetails(item.id); }} className="text-[#2F5E2E] font-bold flex items-center gap-0.5 hover:underline">
                    Edit Plan <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings Side Drawer Overlay */}
      {selectedId !== null && (
        <div
        className="fixed inset-0 bg-black/40 z-50 flex justify-end"
        onClick={() => {
          if (!isMutating) {
            setSelectedId(null);
          }
        }}
      >
        <div
          ref={sidebarRef}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full sm:max-w-md h-full p-4 sm:p-6 shadow-xl flex flex-col justify-between overflow-y-auto"
        >
            
            {loadingDetails ? (
              <div className="h-full flex items-center justify-center text-xs text-gray-500"><RefreshCw className="w-5 h-5 animate-spin mr-2 text-[#2F5E2E]" /> Loading settings...</div>
            ) : details ? (
              <div className="space-y-5 text-[11px] sm:text-xs">
                
                {/* Header Row */}
                <div className="flex justify-between items-center border-b pb-3">
                  <div>
                    <h2 className="text-sm sm:text-base font-black text-gray-900">Manage Subscription</h2>
                    <p className="text-gray-400 text-[10px]">Subscription ID: #{details.id}</p>
                  </div>
                  <button onClick={() => setSelectedId(null)} disabled={isMutating} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
                </div>

                {/* State Control Panel */}
                <div className="bg-gray-50 border rounded-xl p-3.5 space-y-3.5">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-bold text-gray-700 text-[10px] uppercase">Status Controls</span>
                    <span className={`px-2 py-0.5 rounded font-bold uppercase text-[9px] border ${getStatusBadge(details.status)}`}>
                      {details.status === "Active" ? "Active" : details.status === "Paused" ? "Paused" : "Cancelled"}
                    </span>
                  </div>

                  {/* Main Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    {details.status === "Active" ? (
                      <button 
                        onClick={() => confirmAction(
                          "Pause Deliveries?", 
                          "Are you sure you want to pause your deliveries for this plan?", 
                          () => handleMutation(() => pauseSub(details.id).unwrap(), "Plan paused.")
                        )}
                        disabled={isMutating}
                        className="py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold flex items-center justify-center gap-1.5 transition text-xs shadow-xs"
                      >
                        <PauseCircle className="w-4 h-4" /> Pause Deliveries
                      </button>
                    ) : details.status === "Paused" ? (
                      <button 
                        onClick={() => confirmAction(
                          "Resume Plan?", 
                          "Are you sure you want to resume your deliveries?", 
                          () => handleMutation(() => resumeSub(details.id).unwrap(), "Plan resumed.")
                        )}
                        disabled={isMutating}
                        className="py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold flex items-center justify-center gap-1.5 transition text-xs shadow-xs"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Resume Plan
                      </button>
                    ) : null}

                    <button 
                      onClick={() => confirmAction(
                        "Skip Next Shipment?", 
                        "Are you sure you want to skip your next scheduled shipment?", 
                        () => handleMutation(() => skipDelivery(details.id).unwrap(), "Next delivery skipped.")
                      )}
                      disabled={isMutating || details.status !== "Active"}
                      className="py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center justify-center gap-1.5 transition text-xs shadow-xs disabled:opacity-40"
                    >
                      <SkipForward className="w-4 h-4" /> Skip Next Delivery
                    </button>
                  </div>
                </div>

                {/* Inline Change Fields */}
                <div className="border border-[#DDE7D8] bg-white rounded-xl p-3.5 space-y-4">
                  
                  {/* Quantity fields - Min value enforced so customer cannot decrease below current quantity */}
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                    <div>
                      <label className="font-bold text-gray-800 block">Quantity</label>
                      <p className="text-[10px] text-gray-400">Increase items sent per order.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min={details.quantity} 
                        value={editQty} 
                        onChange={e => setEditQty(Math.max(details.quantity, parseInt(e.target.value) || details.quantity))}
                        className="w-16 px-2 py-1 bg-[#FAFBF8] border rounded-md text-center font-bold"
                      />
                      <button 
                        onClick={() => confirmAction(
                          "Update Quantity?", 
                          `Change delivery quantity units to ${editQty}?`, 
                          () => handleMutation(() => updateQty({ id: details.id, data: { quantity: editQty } }).unwrap(), "Quantity updated.")
                        )}
                        disabled={isMutating || editQty === details.quantity || editQty < details.quantity}
                        className="px-3 py-1 bg-[#2F5E2E] text-white font-bold rounded-md hover:bg-[#234722] text-[11px] disabled:opacity-40"
                      >
                        Apply
                      </button>
                    </div>
                  </div>

                  {/* Delivery Cycle dropdown */}
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 border-t pt-3.5">
                    <div>
                      <label className="font-bold text-gray-800 block">Delivery Schedule</label>
                      <p className="text-[10px] text-gray-400">Choose your recurring delivery frequency.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select 
                        value={editCycle} 
                        onChange={e => setEditCycle(parseInt(e.target.value))}
                        className="px-2 py-1 bg-[#FAFBF8] border rounded-md text-xs font-bold focus:outline-hidden"
                      >
                        <option value={1}>Every Week</option>
                        <option value={2}>Every Month</option>
                      </select>
                      <button 
                        onClick={() => confirmAction(
                          "Change Schedule Frequency?", 
                          "Are you sure you want to alter your delivery frequency?", 
                          () => handleMutation(() => changeBilling({ id: details.id, data: { billingCycle: editCycle, interval: details.interval } }).unwrap(), "Delivery frequency updated.")
                        )}
                        disabled={isMutating || editCycle === details.billingCycle}
                        className="px-3 py-1 bg-[#2F5E2E] text-white font-bold rounded-md hover:bg-[#234722] text-[11px] disabled:opacity-40"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                </div>

                {/* Subscribed Product Summary Card */}
                <div className="border rounded-xl p-3 flex gap-3 bg-white">
                  {details.imageUrl && <img src={details.imageUrl} alt="" className="w-12 h-12 object-cover rounded-lg border shrink-0 bg-gray-50" />}
                  <div className="min-w-0 flex-1">
                    <h4 className="font-bold text-gray-900 truncate leading-tight">{details.productName}</h4>
                    <p className="text-gray-400 text-[10px] sm:text-[11px] mt-0.5">Variant: {details.variantName || "Standard"}</p>
                    <p className="text-gray-500 text-[10px] mt-0.5">Next Ship Date: {new Date(details.nextBillingDate).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Past Deliveries Log */}
                <div className="space-y-1.5">
                  <p className="text-gray-400 font-bold text-[9px] uppercase tracking-wider">Past Deliveries ({details.renewals?.length || 0})</p>
                  <div className="border rounded-xl max-h-36 overflow-y-auto divide-y bg-white">
                    {details.renewals?.map((r, i) => (
                      <div key={i} className="p-2.5 flex justify-between items-center text-[10px] sm:text-[11px]">
                        <div>
                          <p className="font-bold text-gray-800">Order #{r.sequenceNumber}</p>
                          <p className="text-gray-400 text-[9px] sm:text-[10px]">{new Date(r.billingDate).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-gray-900 block">AED {r.amount?.toFixed(2)}</span>
                          <span className={`text-[9px] px-1.5 py-0.2 rounded font-bold inline-block ${r.paymentSucceeded ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                            {r.paymentSucceeded ? "Success" : "Failed"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cancel Plan Section: Enabled ONLY after completion of the 1st order */}
                {details.status !== "cancelled" && details.status !== "cancelled" && (
                  <div className="border border-rose-200 bg-rose-50/40 p-3 rounded-xl flex items-center justify-between gap-3 mt-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-rose-900 text-[11px] flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Stop Subscription Permanently
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {hasCompletedFirstOrder 
                          ? "This will cancel all upcoming orders for this plan." 
                          : "Cancellation is available after your 1st order is completed."}
                      </p>
                    </div>
                    <button 
                      onClick={handleCancel}
                      disabled={isMutating || !hasCompletedFirstOrder}
                      className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg transition text-[11px] shrink-0 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                    >
                      Cancel Plan
                    </button>
                  </div>
                )}

              </div>
            ) : null}

            <button onClick={() => setSelectedId(null)} disabled={isMutating} className="w-full bg-[#2F5E2E] hover:bg-[#234722] text-white text-xs font-bold py-2.5 rounded-xl mt-4 disabled:opacity-50 transition shadow-xs cursor-pointer">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSubscriptions;